import { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  Mic, Shield, BarChart3, Users, Clock, CheckCircle, 
  ArrowRight, Play, Mail, Phone, MapPin, ChevronRight,
  Briefcase, Brain, Zap, Target, Award, TrendingUp
} from 'lucide-react';

export default function LandingPage({ onGetStarted }) {
  const [activeFeature, setActiveFeature] = useState(0);

  const features = [
    {
      icon: Brain,
      title: 'AI Resume Scoring',
      desc: 'Intelligent analysis scores candidates against job requirements automatically.',
      detail: 'Our GPT-powered engine evaluates resumes in seconds, identifying key strengths and gaps.'
    },
    {
      icon: Mic,
      title: 'Voice Screening',
      desc: 'Conversational AI conducts technical interviews with natural dialogue.',
      detail: 'Real-time voice interactions with follow-up questions tailored to candidate responses.'
    },
    {
      icon: Shield,
      title: 'Smart Proctoring',
      desc: 'Maintain interview integrity with real-time monitoring.',
      detail: 'Tab switching, copy-paste detection, and idle time tracking ensure fair assessments.'
    },
    {
      icon: Users,
      title: 'Team Collaboration',
      desc: 'Multi-recruiter workspaces with role-based access control.',
      detail: 'Invite team members, assign roles, and collaborate on hiring decisions seamlessly.'
    },
  ];

  const stats = [
    { value: '85%', label: 'Time Saved', desc: 'on initial screening' },
    { value: '3x', label: 'More Candidates', desc: 'screened per day' },
    { value: '95%', label: 'Accuracy', desc: 'in skill matching' },
  ];

  const howItWorks = [
    { step: '01', title: 'Create Job Roles', desc: 'Define requirements, skills, and experience levels for each position.' },
    { step: '02', title: 'Upload Resumes', desc: 'Add candidates via text paste or PDF upload for AI analysis.' },
    { step: '03', title: 'AI Scoring', desc: 'Resumes are automatically scored. Top candidates get shortlisted.' },
    { step: '04', title: 'Auto Scheduling', desc: 'Shortlisted candidates receive emails to self-schedule screenings.' },
    { step: '05', title: 'AI Interviews', desc: 'Conversational AI conducts technical screenings with proctoring.' },
    { step: '06', title: 'Review & Hire', desc: 'Access playback, transcripts, and scores to make informed decisions.' },
  ];

  return (
    <div className="min-h-screen bg-white" data-testid="landing-page" style={{ fontFamily: "'Lora', Georgia, serif" }}>
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 bg-white/95 backdrop-blur-sm border-b border-slate-100 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-[#1e40af] to-[#3b82f6] flex items-center justify-center">
              <Mic className="w-5 h-5 text-white" />
            </div>
            <span className="text-2xl font-semibold text-slate-800" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
              Vocala<span className="text-[#2563eb]">.</span>
            </span>
          </div>
          <div className="hidden md:flex items-center gap-8">
            <a href="#features" className="text-slate-600 hover:text-[#2563eb] text-sm font-medium transition-colors">Features</a>
            <a href="#how-it-works" className="text-slate-600 hover:text-[#2563eb] text-sm font-medium transition-colors">How It Works</a>
            <a href="#contact" className="text-slate-600 hover:text-[#2563eb] text-sm font-medium transition-colors">Contact</a>
          </div>
          <button 
            onClick={onGetStarted}
            data-testid="nav-get-started-btn"
            className="bg-[#2563eb] hover:bg-[#1d4ed8] text-white px-5 py-2.5 rounded-lg text-sm font-medium transition-colors flex items-center gap-2"
          >
            Get Started <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-6 bg-gradient-to-b from-slate-50 to-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div 
              initial={{ opacity: 0, y: 30 }} 
              animate={{ opacity: 1, y: 0 }} 
              transition={{ duration: 0.6 }}
            >
              <div className="inline-flex items-center gap-2 bg-[#2563eb]/10 text-[#2563eb] px-4 py-2 rounded-full text-sm font-medium mb-6">
                <Zap className="w-4 h-4" /> AI-Powered Technical Screening
              </div>
              <h1 
                className="text-5xl lg:text-6xl font-bold text-slate-800 leading-tight mb-6"
                style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
              >
                Hire Smarter.<br />
                <span className="text-[#2563eb]">Screen Faster.</span>
              </h1>
              <p className="text-lg text-slate-600 leading-relaxed mb-8 max-w-xl" style={{ fontFamily: "'Lora', Georgia, serif" }}>
                Transform your technical hiring with AI-powered resume scoring, 
                conversational voice interviews, and intelligent candidate assessment. 
                Built for modern recruiting teams.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <button 
                  onClick={onGetStarted}
                  data-testid="hero-get-started-btn"
                  className="bg-[#2563eb] hover:bg-[#1d4ed8] text-white px-8 py-4 rounded-xl text-base font-medium transition-all shadow-lg shadow-blue-500/25 flex items-center justify-center gap-2"
                >
                  Start Screening <ArrowRight className="w-5 h-5" />
                </button>
                <button className="border-2 border-slate-200 hover:border-[#2563eb] text-slate-700 hover:text-[#2563eb] px-8 py-4 rounded-xl text-base font-medium transition-all flex items-center justify-center gap-2">
                  <Play className="w-5 h-5" /> Watch Demo
                </button>
              </div>
            </motion.div>

            <motion.div 
              initial={{ opacity: 0, x: 30 }} 
              animate={{ opacity: 1, x: 0 }} 
              transition={{ duration: 0.6, delay: 0.2 }}
              className="relative hidden lg:block"
            >
              <div className="relative bg-gradient-to-br from-[#1e40af] to-[#3b82f6] rounded-2xl p-8 shadow-2xl">
                <div className="bg-white rounded-xl p-6 shadow-lg">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 rounded-full bg-[#2563eb]/10 flex items-center justify-center">
                      <Brain className="w-5 h-5 text-[#2563eb]" />
                    </div>
                    <div>
                      <p className="font-semibold text-slate-800">AI Screening in Progress</p>
                      <p className="text-sm text-slate-500">Senior Developer Role</p>
                    </div>
                  </div>
                  <div className="space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-slate-600">Resume Score</span>
                      <span className="text-sm font-semibold text-[#10b981]">8.5/10</span>
                    </div>
                    <div className="w-full bg-slate-100 rounded-full h-2">
                      <div className="bg-[#10b981] h-2 rounded-full" style={{ width: '85%' }} />
                    </div>
                    <div className="flex justify-between items-center pt-2">
                      <span className="text-sm text-slate-600">Proctoring</span>
                      <span className="text-sm font-semibold text-[#2563eb]">98%</span>
                    </div>
                  </div>
                </div>
                <div className="absolute -bottom-4 -right-4 bg-white rounded-xl px-4 py-3 shadow-lg flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-[#10b981]" />
                  <span className="text-sm font-medium text-slate-700">Candidate Shortlisted</span>
                </div>
              </div>
            </motion.div>
          </div>

          {/* Stats */}
          <motion.div 
            initial={{ opacity: 0, y: 30 }} 
            animate={{ opacity: 1, y: 0 }} 
            transition={{ duration: 0.6, delay: 0.4 }}
            className="grid md:grid-cols-3 gap-8 mt-20 pt-12 border-t border-slate-100"
          >
            {stats.map((stat) => (
              <div key={stat.label} className="text-center">
                <p className="text-4xl font-bold text-[#2563eb]" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>{stat.value}</p>
                <p className="text-lg font-semibold text-slate-800 mt-1">{stat.label}</p>
                <p className="text-sm text-slate-500">{stat.desc}</p>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-24 px-6 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-[#2563eb] font-semibold text-sm uppercase tracking-wider mb-3">Powerful Features</p>
            <h2 className="text-4xl font-bold text-slate-800" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
              Everything You Need to<br />Streamline Hiring
            </h2>
          </div>

          <div className="grid lg:grid-cols-2 gap-12 items-start">
            <div className="space-y-4">
              {features.map((feature, i) => (
                <motion.div
                  key={feature.title}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ delay: i * 0.1 }}
                  viewport={{ once: true }}
                  onClick={() => setActiveFeature(i)}
                  className={`p-6 rounded-xl cursor-pointer transition-all ${
                    activeFeature === i 
                      ? 'bg-[#2563eb] text-white shadow-lg shadow-blue-500/25' 
                      : 'bg-slate-50 hover:bg-slate-100 text-slate-800'
                  }`}
                >
                  <div className="flex items-start gap-4">
                    <div className={`w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0 ${
                      activeFeature === i ? 'bg-white/20' : 'bg-[#2563eb]/10'
                    }`}>
                      <feature.icon className={`w-6 h-6 ${activeFeature === i ? 'text-white' : 'text-[#2563eb]'}`} />
                    </div>
                    <div>
                      <h3 className="font-semibold text-lg mb-1">{feature.title}</h3>
                      <p className={`text-sm ${activeFeature === i ? 'text-white/80' : 'text-slate-500'}`}>
                        {feature.desc}
                      </p>
                    </div>
                    <ChevronRight className={`w-5 h-5 flex-shrink-0 ${activeFeature === i ? 'text-white' : 'text-slate-300'}`} />
                  </div>
                </motion.div>
              ))}
            </div>

            <div className="lg:sticky lg:top-32">
              <motion.div
                key={activeFeature}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-gradient-to-br from-slate-50 to-slate-100 rounded-2xl p-8"
              >
                <div className="w-16 h-16 rounded-xl bg-[#2563eb] flex items-center justify-center mb-6 shadow-lg shadow-blue-500/25">
                  {(() => {
                    const Icon = features[activeFeature].icon;
                    return <Icon className="w-8 h-8 text-white" />;
                  })()}
                </div>
                <h3 className="text-2xl font-bold text-slate-800 mb-4" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
                  {features[activeFeature].title}
                </h3>
                <p className="text-slate-600 leading-relaxed text-lg" style={{ fontFamily: "'Lora', Georgia, serif" }}>
                  {features[activeFeature].detail}
                </p>
                <button 
                  onClick={onGetStarted}
                  className="mt-8 text-[#2563eb] font-semibold flex items-center gap-2 hover:gap-3 transition-all"
                >
                  Learn more <ArrowRight className="w-4 h-4" />
                </button>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section id="how-it-works" className="py-24 px-6 bg-slate-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-[#2563eb] font-semibold text-sm uppercase tracking-wider mb-3">Simple Process</p>
            <h2 className="text-4xl font-bold text-slate-800" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
              How Vocala Works
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {howItWorks.map((item, i) => (
              <motion.div
                key={item.step}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.1 }}
                viewport={{ once: true }}
                className="bg-white rounded-xl p-8 shadow-sm hover:shadow-lg transition-shadow"
              >
                <span className="text-5xl font-bold text-[#2563eb]/10" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
                  {item.step}
                </span>
                <h3 className="text-xl font-semibold text-slate-800 mt-4 mb-2">{item.title}</h3>
                <p className="text-slate-500" style={{ fontFamily: "'Lora', Georgia, serif" }}>{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 px-6 bg-gradient-to-br from-[#1e40af] to-[#3b82f6]">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl lg:text-5xl font-bold text-white mb-6" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
              Ready to Transform Your<br />Hiring Process?
            </h2>
            <p className="text-blue-100 text-lg mb-10 max-w-2xl mx-auto" style={{ fontFamily: "'Lora', Georgia, serif" }}>
              Join forward-thinking companies using AI to find the best technical talent faster and more efficiently.
            </p>
            <button 
              onClick={onGetStarted}
              data-testid="cta-get-started-btn"
              className="bg-white text-[#2563eb] hover:bg-slate-50 px-10 py-4 rounded-xl text-lg font-semibold transition-all shadow-lg shadow-blue-900/25 inline-flex items-center gap-2"
            >
              Get Started Now <ArrowRight className="w-5 h-5" />
            </button>
          </motion.div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-24 px-6 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-16">
            <div>
              <p className="text-[#2563eb] font-semibold text-sm uppercase tracking-wider mb-3">Get In Touch</p>
              <h2 className="text-4xl font-bold text-slate-800 mb-6" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
                We're Here to Help
              </h2>
              <p className="text-slate-600 text-lg mb-10" style={{ fontFamily: "'Lora', Georgia, serif" }}>
                Have questions about Vocala? Our team is ready to assist you in transforming your technical hiring process.
              </p>

              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-lg bg-[#2563eb]/10 flex items-center justify-center flex-shrink-0">
                    <Mail className="w-5 h-5 text-[#2563eb]" />
                  </div>
                  <div>
                    <p className="font-semibold text-slate-800">Email Us</p>
                    <p className="text-slate-500">jobs@dstechnologiesinc.com</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-lg bg-[#2563eb]/10 flex items-center justify-center flex-shrink-0">
                    <Phone className="w-5 h-5 text-[#2563eb]" />
                  </div>
                  <div>
                    <p className="font-semibold text-slate-800">Call Us</p>
                    <p className="text-slate-500">+571-765-3020</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-lg bg-[#2563eb]/10 flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-5 h-5 text-[#2563eb]" />
                  </div>
                  <div>
                    <p className="font-semibold text-slate-800">Office Location</p>
                    <p className="text-slate-500">45150 Russell Branch Pkwy, 203-C,<br />Ashburn, VA – 20147</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-slate-50 rounded-2xl p-8">
              <h3 className="text-xl font-semibold text-slate-800 mb-6">Send us a message</h3>
              <form className="space-y-5">
                <div className="grid md:grid-cols-2 gap-5">
                  <div>
                    <label className="text-slate-600 text-sm font-medium block mb-2">First Name</label>
                    <input 
                      type="text" 
                      className="w-full bg-white border border-slate-200 rounded-lg px-4 py-3 text-slate-800 placeholder-slate-400 focus:border-[#2563eb] focus:ring-2 focus:ring-[#2563eb]/20 outline-none transition-all"
                      placeholder="John"
                    />
                  </div>
                  <div>
                    <label className="text-slate-600 text-sm font-medium block mb-2">Last Name</label>
                    <input 
                      type="text" 
                      className="w-full bg-white border border-slate-200 rounded-lg px-4 py-3 text-slate-800 placeholder-slate-400 focus:border-[#2563eb] focus:ring-2 focus:ring-[#2563eb]/20 outline-none transition-all"
                      placeholder="Doe"
                    />
                  </div>
                </div>
                <div>
                  <label className="text-slate-600 text-sm font-medium block mb-2">Email</label>
                  <input 
                    type="email" 
                    className="w-full bg-white border border-slate-200 rounded-lg px-4 py-3 text-slate-800 placeholder-slate-400 focus:border-[#2563eb] focus:ring-2 focus:ring-[#2563eb]/20 outline-none transition-all"
                    placeholder="john@company.com"
                  />
                </div>
                <div>
                  <label className="text-slate-600 text-sm font-medium block mb-2">Message</label>
                  <textarea 
                    rows={4}
                    className="w-full bg-white border border-slate-200 rounded-lg px-4 py-3 text-slate-800 placeholder-slate-400 focus:border-[#2563eb] focus:ring-2 focus:ring-[#2563eb]/20 outline-none transition-all resize-none"
                    placeholder="How can we help you?"
                  />
                </div>
                <button 
                  type="button"
                  className="w-full bg-[#2563eb] hover:bg-[#1d4ed8] text-white py-4 rounded-lg font-semibold transition-colors"
                >
                  Send Message
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 py-16 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-4 gap-12 mb-12">
            <div className="md:col-span-2">
              <div className="flex items-center gap-2 mb-4">
                <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-[#2563eb] to-[#3b82f6] flex items-center justify-center">
                  <Mic className="w-5 h-5 text-white" />
                </div>
                <span className="text-2xl font-semibold text-white" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
                  Vocala<span className="text-[#3b82f6]">.</span>
                </span>
              </div>
              <p className="text-slate-400 max-w-md" style={{ fontFamily: "'Lora', Georgia, serif" }}>
                AI-powered technical screening platform. Streamline your hiring process with intelligent resume scoring and conversational interviews.
              </p>
            </div>
            <div>
              <h4 className="text-white font-semibold mb-4">Quick Links</h4>
              <div className="space-y-3">
                <a href="#features" className="block text-slate-400 hover:text-white transition-colors">Features</a>
                <a href="#how-it-works" className="block text-slate-400 hover:text-white transition-colors">How It Works</a>
                <a href="#contact" className="block text-slate-400 hover:text-white transition-colors">Contact</a>
              </div>
            </div>
            <div>
              <h4 className="text-white font-semibold mb-4">Company</h4>
              <div className="space-y-3">
                <a href="https://www.dstechnologiesinc.com" target="_blank" rel="noopener noreferrer" className="block text-slate-400 hover:text-white transition-colors">DS Technologies</a>
                <a href="#" className="block text-slate-400 hover:text-white transition-colors">Privacy Policy</a>
                <a href="#" className="block text-slate-400 hover:text-white transition-colors">Terms of Service</a>
              </div>
            </div>
          </div>
          <div className="border-t border-slate-800 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-slate-500 text-sm">© {new Date().getFullYear()} Vocala. A DS Technologies Product. All Rights Reserved.</p>
            <div className="flex items-center gap-6">
              <a href="https://www.linkedin.com/company/ds-technologiesinc/" target="_blank" rel="noopener noreferrer" className="text-slate-400 hover:text-white transition-colors">
                LinkedIn
              </a>
              <a href="https://twitter.com/dstechinc" target="_blank" rel="noopener noreferrer" className="text-slate-400 hover:text-white transition-colors">
                Twitter
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
